import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { getFileIcon } from "../../utils/fileIconUtils";
import { getLinkIcon } from "../../utils/businessCardUtils";
import {
  HiChevronDown,
  HiChevronUp,
  HiOutlineDocumentText,
  HiOutlineDownload,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineUser,
} from "react-icons/hi";
import VCardGenerator from "./VCardGenerator";
import { LinkItem } from "./LinkManager";
import BusinessCardFooter from "./BusinessCardFooter";
import BusinessCardShareButton from "./BusinessCardShareButton";
import UserRegistrationSection from "./UserRegistrationSection";
import BookingLinkButton from "./BookingLinkButton";
import { BusinessCardBooking } from "../../types/BusinessCardTypes";
import BusinessCardContainer from "./BusinessCardContainer";
import BusinessCardEmailLogin from "../pages/businessCardView/BusinessCardEmailLogin";
import ExpandableDescription from "./ExpandableDescription";
import LanguageSelector from "./LanguageSelector";
import SmartLink from "./SmartLink";
import PreviewSafeAnchor from "./PreviewSafeAnchor";
import PreviewSafeButton from "./PreviewSafeButton";

interface BusinessCardData {
  jobTitle: string;
  description?: string;
  links: LinkItem[];
  uploadedFiles?: Array<{
    fileName: string;
    cdnPath: string;
    displayName: string;
  }>;
  allowVCardDownload?: boolean;
  enableUserRegistration?: boolean;
  showFooter?: boolean;
  booking?: BusinessCardBooking;
}

interface BusinessCardLuxuryDisplayProps {
  userData: {
    user_identification: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    avatar?: string;
  };
  businessCardData: BusinessCardData;
  companyLogo?: string;
  companyName?: string;
  companyDescription?: string;
  className?: string;
  isPreview?: boolean;
  processId?: string;
  enableUserRegistration?: boolean;
  entityIdentification: string;
  ownerIdentification: string;
  onEmailClick?: () => void;
}

const BusinessCardLuxuryDisplay: React.FC<BusinessCardLuxuryDisplayProps> = (
  props
) => {
  const {
    userData,
    businessCardData,
    companyLogo,
    companyName,
    companyDescription,
    className = "",
    isPreview = false,
    processId,
    enableUserRegistration = false,
    entityIdentification,
    ownerIdentification,
    onEmailClick,
  } = props;
  const { t } = useTranslation("clientView");
  const [showAllLinks, setShowAllLinks] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);

  const handleFlipToEmail = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setShowEmailLogin(true);
      onEmailClick?.();
    }, 300);
    setTimeout(() => setIsFlipping(false), 600);
  };

  const handleFileDownload = (cdnPath: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = cdnPath;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const primaryLinks = businessCardData.links?.slice(0, 3) || [];
  const additionalLinks = businessCardData.links?.slice(3) || [];
  const hasFiles =
    businessCardData.uploadedFiles && businessCardData.uploadedFiles.length > 0;

  const headerContent = (companyName || companyLogo || companyDescription) && (
    <div className="border-b border-yellow-400">
      <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-400 relative">
        <div className="absolute top-2 end-2 z-[9999]">
          <LanguageSelector variant="compact" />
        </div>
        <div className="mt-6">
          {companyName && (
            <p
              className={`${
                companyName.length > 20 ? "text-xs" : "text-base"
              } font-serif font-bold text-amber-900 uppercase tracking-wider text-start`}
            >
              <bdi>{companyName}</bdi>
            </p>
          )}
          {(companyLogo || companyDescription) && (
            <div className="flex items-start gap-2 mt-1">
              {companyLogo && (
                <div className="w-10 h-10 flex-shrink-0">
                  <img
                    src={companyLogo}
                    alt="Company Logo"
                    className="w-full h-full object-contain rounded-lg bg-white shadow-sm border border-amber-600"
                  />
                </div>
              )}
              {companyDescription && (
                <p className="text-xs text-amber-800 font-serif italic text-start">
                  <bdi>"{companyDescription}"</bdi>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const mainContent = showEmailLogin ? (
    <div className="flex flex-col gap-3 sm:gap-4">
      <BusinessCardEmailLogin
        entityIdentification={entityIdentification}
        ownerIdentification={ownerIdentification}
        onBackToCard={() => {
          if (isFlipping) return;
          setIsFlipping(true);
          setTimeout(() => setShowEmailLogin(false), 300);
          setTimeout(() => setIsFlipping(false), 600);
        }}
      />
    </div>
  ) : (
    <div className="flex flex-col gap-3 sm:gap-4">
      <div className="pb-3 border-b bg-gradient-to-r from-amber-900 to-yellow-900 text-white rounded-xl p-4 border-2 border-yellow-400 shadow-lg">
        <div className="flex items-center gap-4">
          {userData.avatar ? (
            <img
              src={userData.avatar}
              alt={`${userData.first_name} ${userData.last_name}`}
              className="w-10 h-10 ring-4 ring-yellow-400 rounded-sm"
            />
          ) : (
            <div className="w-10 h-10 ring-4 ring-yellow-400 rounded-sm flex items-center justify-center bg-amber-50">
              {userData.first_name?.[0] || userData.last_name?.[0] ? (
                <span className="text-amber-700 text-base font-bold uppercase">
                  {userData.first_name?.[0]}
                  {userData.last_name?.[0]}
                </span>
              ) : (
                <HiOutlineUser className="w-5 h-5 text-amber-600" />
              )}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-serif font-bold text-yellow-400 break-words">
              <bdi>
                {userData.first_name} {userData.last_name}
              </bdi>
            </h1>
            <p className="text-sm text-amber-200 break-words font-serif italic text-start">
              <bdi>{businessCardData.jobTitle}</bdi>
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <PreviewSafeAnchor
          href={`mailto:${userData.email}`}
          className="grid grid-cols-[auto_1fr] gap-2 sm:gap-3 w-full p-2.5 sm:p-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-lg hover:from-amber-500 hover:to-yellow-500 transition-all duration-200 border-2 border-yellow-400 shadow-lg items-center"
        >
          <HiOutlineMail className="w-5 h-5" />
          <div className="min-w-0">
            <p
              dir="auto"
              className="text-xs text-amber-100 font-medium text-start"
            >
              {t("key98")}
            </p>
            <p
              className="font-serif font-medium text-xs sm:text-sm truncate"
              title={userData.email}
            >
              <bdi>{userData.email}</bdi>
            </p>
          </div>
        </PreviewSafeAnchor>

        {userData.phone && (
          <PreviewSafeAnchor
            href={`tel:${userData.phone}`}
            className="flex items-center gap-2 sm:gap-3 w-full p-2.5 sm:p-3 bg-gradient-to-r from-yellow-600 to-amber-600 text-white rounded-lg hover:from-yellow-500 hover:to-amber-500 transition-all duration-200 border-2 border-yellow-400 shadow-lg"
          >
            <HiOutlinePhone className="w-5 h-5" />
            <p className="font-serif font-medium text-xs sm:text-sm">
              <bdi>{userData.phone}</bdi>
            </p>
          </PreviewSafeAnchor>
        )}
      </div>

      {businessCardData.description && (
        <div className="border-t border-yellow-400 pt-3 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-xl p-4 border-2 shadow-lg">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-3 h-3 bg-yellow-400 rounded-full border border-amber-600"></div>
            <p className="text-sm font-serif font-bold text-amber-900 uppercase tracking-widest text-start">
              {t("businessCard.sections.excellence").toUpperCase()}
            </p>
            <div className="w-3 h-3 bg-amber-400 rounded-full border border-yellow-600"></div>
          </div>

          <ExpandableDescription
            content={businessCardData.description}
            maxLength={150}
            maxLines={5}
            textClassName="text-sm text-amber-800 leading-relaxed font-serif italic"
            buttonClassName="mt-2 inline-flex items-center text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors duration-200"
            expandText={t("key97") as string}
            collapseText={t("key107") as string}
          />
        </div>
      )}

      {primaryLinks.length > 0 && (
        <div className="space-y-2">
          <div className="text-center">
            <p
              dir="auto"
              className="text-sm font-serif font-bold text-amber-900 uppercase tracking-widest bg-gradient-to-r from-yellow-300 to-amber-300 inline-block px-4 py-1 rounded-full border-2 border-yellow-400 text-start"
            >
              ♦ {t("businessCard.sections.connections").toUpperCase()} ♦
            </p>
          </div>
          {primaryLinks.map((link, index) => {
            const luxuryGradients = [
              "from-amber-600 to-yellow-600",
              "from-yellow-600 to-amber-600",
              "from-amber-700 to-yellow-700",
            ];
            const gradient = luxuryGradients[index % luxuryGradients.length];

            return (
              <SmartLink
                key={index}
                href={link.link}
                fallbackHref={link.fallbackLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 sm:gap-3 p-2 bg-gradient-to-r ${gradient} hover:scale-105 text-white rounded-lg transition-all duration-200 group border-2 border-yellow-400 shadow-lg`}
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
                  {getLinkIcon(link, "text-amber-600")}
                </div>
                <p className="flex-1 text-sm font-serif font-bold text-white group-hover:text-yellow-200 truncate text-start">
                  {link.label}
                </p>
                <div className="text-yellow-300 font-serif font-bold">→</div>
              </SmartLink>
            );
          })}
        </div>
      )}

      {additionalLinks.length > 0 && (
        <div>
          {showAllLinks && (
            <div className="space-y-2 mb-2">
              {additionalLinks.map((link, index) => {
                const luxuryGradients = [
                  "from-amber-500 to-yellow-500",
                  "from-yellow-500 to-orange-500",
                  "from-orange-600 to-amber-600",
                ];
                const gradient =
                  luxuryGradients[index % luxuryGradients.length];

                return (
                  <SmartLink
                    key={index + 3}
                    href={link.link}
                    fallbackHref={link.fallbackLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 sm:gap-3 p-2 bg-gradient-to-r ${gradient} hover:scale-105 text-white rounded-lg transition-all duration-200 group border-2 border-yellow-400 shadow-lg`}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
                      {getLinkIcon(link, "text-amber-600")}
                    </div>
                    <p className="flex-1 text-sm font-serif font-bold text-white truncate group-hover:text-yellow-200 text-start">
                      {link.label}
                    </p>
                    <div className="text-yellow-300 font-serif font-bold">
                      →
                    </div>
                  </SmartLink>
                );
              })}
            </div>
          )}
          <PreviewSafeButton
            onClick={() => setShowAllLinks(!showAllLinks)}
            className="flex items-center justify-center w-full p-2 text-sm text-amber-900 font-serif font-bold bg-gradient-to-r from-yellow-200 to-amber-200 hover:from-yellow-300 hover:to-amber-300 rounded-lg transition-colors duration-200 border-2 border-yellow-400"
            dir="auto"
          >
            <span className="me-1 text-start">
              {showAllLinks
                ? t("businessCard.showLess")
                : t("businessCard.showMoreLinks", {
                    count: additionalLinks.length,
                  })}
            </span>
            {showAllLinks ? (
              <HiChevronUp className="w-4 h-4" />
            ) : (
              <HiChevronDown className="w-4 h-4" />
            )}
          </PreviewSafeButton>
        </div>
      )}

      {hasFiles && (
        <div>
          <PreviewSafeButton
            onClick={() => setShowFiles(!showFiles)}
            className="flex items-center justify-between w-full p-2 text-sm text-amber-900 font-serif font-bold bg-gradient-to-r from-amber-200 to-yellow-200 hover:from-amber-300 hover:to-yellow-300 rounded-lg transition-colors duration-200 border-2 border-yellow-400"
            dir="auto"
          >
            <div className="flex items-center gap-2">
              <HiOutlineDocumentText className="w-4 h-4" />
              <span className="text-start">
                {t("businessCard.files", {
                  count: businessCardData.uploadedFiles?.length || 0,
                })}
              </span>
            </div>
            {showFiles ? (
              <HiChevronUp className="w-4 h-4" />
            ) : (
              <HiChevronDown className="w-4 h-4" />
            )}
          </PreviewSafeButton>

          {showFiles && businessCardData.uploadedFiles && (
            <div className="mt-2 space-y-1">
              {businessCardData.uploadedFiles.map((file, index) => (
                <PreviewSafeButton
                  key={index}
                  onClick={() =>
                    handleFileDownload(file.cdnPath, file.fileName)
                  }
                  className="w-full flex items-center gap-2 p-2 bg-gradient-to-r from-amber-100 to-yellow-100 hover:from-amber-200 hover:to-yellow-200 rounded-lg transition-colors duration-200 text-left group border border-yellow-400"
                  dir="auto"
                >
                  <span className="text-sm">{getFileIcon(file.fileName)}</span>
                  <p className="flex-1 text-sm font-serif font-bold text-amber-900 truncate group-hover:text-amber-800 text-start">
                    {file.displayName}
                  </p>
                  <HiOutlineDownload className="w-4 h-4 text-amber-600 group-hover:text-amber-700" />
                </PreviewSafeButton>
              ))}
            </div>
          )}
        </div>
      )}

      {(() => {
        const showSaveContact = businessCardData.allowVCardDownload === true;
        const showShare = !!processId;
        if (!showSaveContact && !showShare) return null;

        return (
          <div
            className={`grid gap-2 ${
              showSaveContact && showShare ? "grid-cols-2" : "grid-cols-1"
            }`}
          >
            {showSaveContact && (
              <VCardGenerator
                data={{
                  firstName: userData.first_name,
                  lastName: userData.last_name,
                  jobTitle: businessCardData.jobTitle,
                  email: userData.email,
                  phone: userData.phone,
                  description: businessCardData.description,
                  links: businessCardData.links,
                  avatar: userData.avatar,
                }}
                buttonColor="amber"
              />
            )}
            {showShare && (
              <BusinessCardShareButton
                processId={processId}
                size="md"
                variant="secondary"
                logoUrl={companyLogo}
              />
            )}
          </div>
        );
      })()}

      <BookingLinkButton
        booking={businessCardData.booking}
        entityIdentification={entityIdentification}
        memberId={userData.user_identification}
      />

      {enableUserRegistration && processId && entityIdentification && (
        <UserRegistrationSection
          processId={processId}
          entityData={{
            entity_identification: entityIdentification,
            entity_name: companyName,
            entity_logo: companyLogo,
          }}
          theme="classic"
          size="sm"
          onEmailLogin={handleFlipToEmail}
        />
      )}
    </div>
  );

  const frontContent = (
    <div className="w-full bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl border-4 border-yellow-400 shadow-2xl">
      <div className="w-full min-h-full relative rounded-lg overflow-hidden">
        {headerContent && (
          <div className="row-start-1 row-end-2">{headerContent}</div>
        )}

        <div className="row-start-2 row-end-3">
          <div className="px-3 sm:px-4 py-3 sm:py-4">{mainContent}</div>
        </div>

        <div className="row-start-3 row-end-4">
          <BusinessCardFooter
            showFooter={businessCardData.showFooter as boolean}
            theme="luxury"
          />
        </div>
      </div>
    </div>
  );

  return (
    <BusinessCardContainer
      isPreview={isPreview}
      className={`w-full h-auto min-h-0 ${className}`}
      frontContent={frontContent}
      isFlipping={isFlipping}
      showEmailLogin={showEmailLogin}
    />
  );
};

export default BusinessCardLuxuryDisplay;
